import * as React from 'react';
import { useSelector } from 'react-redux';

import { useDeviceType } from '@cian/ui-kit/responsive';

import { MainScreenMobile } from '../../components/MainScreen';
import { ProgressBarContainer } from '../ProgressBarContainer';
import { Provider, useFormController, FormContainer } from '../FormContainer';
import { BackToPageContainer } from '../BackToPageContainer';
import { TFormStoreData } from '../../types/form';
import { prevQuestionTexts } from '../../constants/prevQuestionTexts';
import { submitForm } from '../../actions/submitForm';
import { useOnRenderedInBrowser } from './utils/useOnRenderedInBrowser';
import { PageBaseLayout } from '../../components/PageBaseLayout';
import { showGoBackBtn } from './utils/showGoBackBtn';
import { IntroContainer } from '../IntroContainer';
import { MainScreenDesktopContainer } from '../MainScreenDesktopContainer';
import { selectWithIntroScreen } from '../../selectors/selectWithIntroScreen';
import { useSmartContactPixel } from './utils/useSmartContactPixel';

interface IPageContainerWithDataProps {
  formData: TFormStoreData;
}

export const PageContainerWithData: React.FC<React.PropsWithChildren<IPageContainerWithDataProps>> = ({ formData }) => {
  const formContext = useFormController(formData, submitForm);
  const { setPrevStep, quizStarted, quizCompleted, stepsHistory } = formContext;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const withIntroScreen = useSelector(selectWithIntroScreen(isDesktop));
  const MainScreen = isDesktop ? MainScreenDesktopContainer : MainScreenMobile;
  useSmartContactPixel(quizCompleted);

  useOnRenderedInBrowser(formData);

  const main = React.useMemo(() => {
    if (quizCompleted) {
      return <FormContainer />;
    }

    return (
      <MainScreen
        main={withIntroScreen && !quizStarted ? <IntroContainer /> : <FormContainer />}
        backToPage={<BackToPageContainer />}
        progressBar={quizStarted || deviceType === 'desktop' ? <ProgressBarContainer /> : null}
        showGoBackBtn={showGoBackBtn(stepsHistory)}
        handleGoBack={setPrevStep}
        goBackText={prevQuestionTexts[deviceType]}
      />
    );
  }, [quizCompleted, MainScreen, withIntroScreen, quizStarted, deviceType, stepsHistory, setPrevStep]);

  return (
    <Provider value={formContext}>
      <PageBaseLayout quizCompleted={formContext.quizCompleted}>{main}</PageBaseLayout>
    </Provider>
  );
};

PageContainerWithData.displayName = 'PageContainerWithData';
